import React from 'react';
import { Card, Table, Pagination, Tooltip, Button } from 'antd';
import { DownloadOutlined, MessageOutlined, ReloadOutlined } from '@ant-design/icons';
import { fonts } from '../theme';
import { formatProjectDisplayName } from '../utils/helpers';
import {
  getStarterKitIconClassName,
  getStarterKitIconType,
  getStarterKitWrapClassName,
  KIT_ICON_DOWNLOADED,
  KIT_ICON_NEUTRAL,
  KIT_ICON_PENDING,
} from '../starter_kit/starterKitStatus';

export const PROJECT_DESCRIPTION_PREVIEW_MAX = 30;

const PROJECT_CARD_ICON_BTN_SIZE = 36;
const PROJECT_CARD_ICON_SIZE = 18;
/** Match reference: reload icon slightly larger than chat/download in the same 36px button. */
const PROJECT_CARD_REGENERATE_ICON_SIZE = 20;

function projectCardIconButtonStyle(themeStyles) {
  return {
    padding: 4,
    height: PROJECT_CARD_ICON_BTN_SIZE,
    width: PROJECT_CARD_ICON_BTN_SIZE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    border: `1px solid ${themeStyles.projectChatBtnBorder}`,
    background: themeStyles.projectChatBtnBg,
  };
}

function ProjectCardIconButton({
  themeStyles,
  icon,
  tooltip,
  ariaLabel,
  className,
  disabled = false,
  loading = false,
  onClick,
}) {
  return (
    <Tooltip title={tooltip}>
      <Button
        type="text"
        className={className}
        icon={icon}
        disabled={disabled}
        loading={loading}
        aria-label={ariaLabel}
        onClick={(e) => {
          e.stopPropagation();
          onClick(e);
        }}
        style={projectCardIconButtonStyle(themeStyles)}
      />
    </Tooltip>
  );
}

function StarterKitTooltipTitle({ iconVisualState }) {
  if (iconVisualState === KIT_ICON_PENDING) {
    return (
      <div className="project-starter-kit-tooltip">
        <div className="project-starter-kit-tooltip__status project-starter-kit-tooltip__status--pending">
          Pending
        </div>
        <div className="project-starter-kit-tooltip__action">Regenerate Starter Kit</div>
      </div>
    );
  }
  if (iconVisualState === KIT_ICON_DOWNLOADED) {
    return (
      <div className="project-starter-kit-tooltip">
        <div className="project-starter-kit-tooltip__status project-starter-kit-tooltip__status--active">
          Activated
        </div>
        <div className="project-starter-kit-tooltip__action">Regenerate Starter Kit</div>
      </div>
    );
  }
  return 'Generate starter kit';
}

function StarterKitIconButton({
  themeStyles,
  busy = false,
  projectName,
  iconVisualState = KIT_ICON_NEUTRAL,
  tooltip = 'Generate starter kit',
  onDownload,
  size = PROJECT_CARD_ICON_BTN_SIZE,
}) {
  const dim = size;
  const iconSize = size === PROJECT_CARD_ICON_BTN_SIZE ? PROJECT_CARD_ICON_SIZE : 16;
  const iconType = getStarterKitIconType(iconVisualState);
  const tooltipTitle = iconVisualState === KIT_ICON_NEUTRAL
    ? tooltip
    : <StarterKitTooltipTitle iconVisualState={iconVisualState} />;

  return (
    <Tooltip title={tooltipTitle} overlayClassName="project-starter-kit-tooltip-overlay">
      <span className={getStarterKitWrapClassName(iconVisualState)}>
        <Button
          type="text"
          className={getStarterKitIconClassName()}
          disabled={busy}
          loading={busy}
          aria-label={`${tooltip} — ${projectName}`}
          icon={iconType === 'reload' ? (
            <ReloadOutlined
              className="project-starter-kit-btn__reload-icon"
              style={{
                fontSize: size === PROJECT_CARD_ICON_BTN_SIZE
                  ? PROJECT_CARD_REGENERATE_ICON_SIZE
                  : 17,
                color: themeStyles.textSecondary,
              }}
            />
          ) : (
            <DownloadOutlined
              style={{
                fontSize: iconSize,
                color: themeStyles.textSecondary,
              }}
            />
          )}
          onClick={(e) => {
            e.stopPropagation();
            onDownload();
          }}
          style={{
            ...projectCardIconButtonStyle(themeStyles),
            height: dim,
            width: dim,
          }}
        />
      </span>
    </Tooltip>
  );
}

/** Shared badge shape — colors come from `themeStyles` only. */
const BADGE_LAYOUT = {
  padding: '4px 8px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: '16px',
  fontFamily: fonts.primary,
  whiteSpace: 'nowrap',
};

export function getDeployBadgeStyle(themeStyles, deployed) {
  if (deployed) {
    return {
      ...BADGE_LAYOUT,
      background: themeStyles.badgeDeployedBg,
      color: themeStyles.badgeDeployedFg,
      border: `1px solid ${themeStyles.badgeDeployedBorder}`,
    };
  }
  return {
    ...BADGE_LAYOUT,
    background: themeStyles.badgeNotDeployedBg,
    color: themeStyles.badgeNotDeployedFg,
    border: `1px solid ${themeStyles.badgeNotDeployedBorder}`,
  };
}

export function formatProjectDescriptionPreview(text) {
  const s = text == null ? '' : String(text);
  if (s.length <= PROJECT_DESCRIPTION_PREVIEW_MAX) {
    return { display: s, full: s, isTruncated: false };
  }
  return {
    display: `${s.slice(0, PROJECT_DESCRIPTION_PREVIEW_MAX)}…`,
    full: s,
    isTruncated: true,
  };
}

export function getVectorCollectionName(record) {
  const name = record?.config?.vector_store?.collection_name;
  if (name && String(name).trim()) return String(name).trim();
  if (record?.project_id) return `rag_${record.project_id}`;
  return '—';
}

export function getProjectRowKey(record) {
  return record.project_id || record.rbac_id || record._id;
}

/** Group admin usernames for a project (from RBAC list / by-core API). */
export function formatProjectAdminNames(project) {
  const names = Array.isArray(project?.admin_names)
    ? project.admin_names.map((n) => String(n || '').trim()).filter(Boolean)
    : [];
  return names.length ? names.join(', ') : '—';
}

export function buildProjectTableColumns(
  themeStyles,
  {
    showAdminColumn = false,
    onDownloadStarterKit = null,
    starterKitBusyProjectId = null,
    getProjectKitState = null,
    getProjectKitIconVisualState = null,
    getStarterKitIconTooltip = null,
  } = {},
) {
  const nameColor = themeStyles?.textPrimary;
  return [
    {
      title: '#',
      key: 'serial',
      width: 56,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Project Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text) => (
        <span style={{ fontWeight: 600, color: nameColor || 'inherit' }}>
          {formatProjectDisplayName(text, { fallback: '—' })}
        </span>
      ),
    },
    {
      title: 'Group',
      dataIndex: 'group_name',
      key: 'group_name',
      ellipsis: true,
      render: (name, record) => name || record.group_id || '—',
    },
    ...(showAdminColumn
      ? [{
        title: 'Admin',
        key: 'admin_names',
        width: 140,
        ellipsis: true,
        render: (_, record) => formatProjectAdminNames(record),
      }]
      : []),
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (t) => t || '—',
    },
    {
      title: 'VectorDB collection',
      key: 'vector_collection',
      ellipsis: true,
      render: (_, record) => {
        const label = getVectorCollectionName(record);
        return (
          <span
            title={label !== '—' ? label : undefined}
            style={{ fontFamily: fonts.mono, fontSize: 12 }}
          >
            {label}
          </span>
        );
      },
    },
    {
      title: 'Created On',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => {
        if (!date) return '—';
        const d = new Date(date);
        if (Number.isNaN(d.getTime())) return '—';
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      },
    },
    {
      title: 'Status',
      dataIndex: 'deployed',
      key: 'deployed',
      width: 140,
      render: (deployed) => {
        const { color, fontSize, fontWeight, fontFamily, whiteSpace } = getDeployBadgeStyle(themeStyles, deployed);
        return (
          <span style={{ color, fontSize, fontWeight, fontFamily, whiteSpace }}>
            {deployed ? 'Deployed' : 'Not Deployed'}
          </span>
        );
      },
    },
    ...(onDownloadStarterKit
      ? [{
        title: 'Starter Kit',
        key: 'starter_kit',
        width: 56,
        render: (_, record) => (
          record.deployed ? (
            <div onClick={(e) => e.stopPropagation()} role="presentation">
              <StarterKitIconButton
                themeStyles={themeStyles}
                busy={starterKitBusyProjectId === record.project_id}
                projectName={formatProjectDisplayName(record.name || record.project_id, { fallback: record.project_id || '—' })}
                iconVisualState={getProjectKitIconVisualState
                  ? getProjectKitIconVisualState(record.project_id)
                  : KIT_ICON_NEUTRAL}
                tooltip={getStarterKitIconTooltip
                  ? getStarterKitIconTooltip(record.project_id)
                  : 'Generate starter kit'}
                onDownload={() => onDownloadStarterKit(record)}
                size={32}
              />
            </div>
          ) : '—'
        ),
      }]
      : []),
  ];
}

function ProjectDirectoryCard({
  project,
  themeStyles,
  onOpenProject,
  onOpenChat,
  onDownloadStarterKit = null,
  starterKitBusy = false,
  iconVisualState = KIT_ICON_NEUTRAL,
  tooltip = 'Generate starter kit',
  showChatShortcut = true,
  showAdmin = false,
}) {
  const shell = {
    background: themeStyles.projectCardBg,
    border: `1px solid ${themeStyles.projectCardBorder}`,
    boxShadow: themeStyles.projectCardShadow,
    borderRadius: 12,
    width: '100%',
    height: '100%',
    cursor: 'pointer',
    position: 'relative',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
  };

  const { display, full, isTruncated } = formatProjectDescriptionPreview(project.description);
  const descStyle = {
    marginTop: 6,
    fontSize: 13,
    color: themeStyles.textSecondary,
    lineHeight: 1.5,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    textAlign: 'left',
  };
  const descNode = <div style={descStyle}>{display}</div>;

  const groupLabel = project.group_name || project.group_id;
  const showChat = Boolean(project.deployed) && showChatShortcut;
  const showStarterKit = Boolean(project.deployed) && typeof onDownloadStarterKit === 'function';
  const hasActions = showChat || showStarterKit;
  const bodyPaddingBottom = hasActions ? 56 : 16;
  const bodyPaddingRight = 16;

  return (
    <Card
      hoverable
      className="project-directory-card"
      onClick={() => onOpenProject(project)}
      style={shell}
      bodyStyle={{
        position: 'relative',
        padding: `16px ${bodyPaddingRight}px ${bodyPaddingBottom}px 16px`,
        minHeight: 140,
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: themeStyles.textPrimary,
            lineHeight: 1.35,
            textAlign: 'left',
            minWidth: 0,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {formatProjectDisplayName(project.name)}
        </span>
        <span
          style={{
            ...getDeployBadgeStyle(themeStyles, project.deployed),
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          {project.deployed ? 'Deployed' : 'Not Deployed'}
        </span>
      </div>

      <div>
        {isTruncated ? <Tooltip title={full}>{descNode}</Tooltip> : descNode}
      </div>

      {groupLabel ? (
        <div
          style={{
            marginTop: 4,
            fontSize: 12,
            color: themeStyles.textTertiary,
            lineHeight: 1.45,
            textAlign: 'left',
          }}
        >
          Group: {groupLabel}
        </div>
      ) : null}

      {showAdmin ? (
        <div
          style={{
            marginTop: 4,
            fontSize: 12,
            color: themeStyles.textTertiary,
            lineHeight: 1.45,
            textAlign: 'left',
          }}
        >
          Admin: {formatProjectAdminNames(project)}
        </div>
      ) : null}

      {hasActions ? (
        <div
          style={{
            position: 'absolute',
            left: 16,
            right: 12,
            bottom: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
            zIndex: 2,
          }}
        >
          {showStarterKit ? (
            <StarterKitIconButton
              themeStyles={themeStyles}
              busy={starterKitBusy}
              projectName={formatProjectDisplayName(project.name)}
              iconVisualState={iconVisualState}
              tooltip={tooltip}
              onDownload={() => onDownloadStarterKit(project)}
            />
          ) : null}
          {showChat ? (
            <ProjectCardIconButton
              themeStyles={themeStyles}
              tooltip="Chat"
              ariaLabel={`Chat with ${formatProjectDisplayName(project.name)}`}
              icon={(
                <MessageOutlined
                  style={{ fontSize: PROJECT_CARD_ICON_SIZE, color: themeStyles.textSecondary }}
                />
              )}
              onClick={() => onOpenChat(project)}
            />
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

export function ProjectCardGrid({
  projects,
  themeStyles,
  onOpenProject,
  onOpenChat,
  onDownloadStarterKit = null,
  starterKitBusyProjectId = null,
  getProjectKitState = null,
  getProjectKitIconVisualState = null,
  getStarterKitIconTooltip = null,
  showChatShortcut = true,
  showAdmin = false,
}) {
  return (
    <div
      className="project-directory-card-grid"
      style={{
        '--pd-card-hover-shadow': themeStyles.projectCardHoverShadow,
        '--pd-card-hover-border': themeStyles.projectCardHoverBorder,
      }}
    >
      {projects.map((project) => (
        <div className="project-directory-card-cell" key={getProjectRowKey(project)}>
          <ProjectDirectoryCard
            project={project}
            themeStyles={themeStyles}
            onOpenProject={onOpenProject}
            onOpenChat={onOpenChat}
            onDownloadStarterKit={onDownloadStarterKit}
            starterKitBusy={starterKitBusyProjectId === project.project_id}
            iconVisualState={getProjectKitIconVisualState
              ? getProjectKitIconVisualState(project.project_id)
              : KIT_ICON_NEUTRAL}
            tooltip={getStarterKitIconTooltip
              ? getStarterKitIconTooltip(project.project_id)
              : 'Generate starter kit'}
            showChatShortcut={showChatShortcut}
            showAdmin={showAdmin}
          />
        </div>
      ))}
    </div>
  );
}

export function ProjectTableView({
  projects,
  onOpenProject,
  themeStyles,
  pagination = { pageSize: 10, showSizeChanger: false, hideOnSinglePage: true },
  showAdminColumn = false,
  onDownloadStarterKit = null,
  starterKitBusyProjectId = null,
  getProjectKitState = null,
  getProjectKitIconVisualState = null,
  getStarterKitIconTooltip = null,
}) {
  return (
    <Table
      columns={buildProjectTableColumns(themeStyles, {
        showAdminColumn,
        onDownloadStarterKit,
        starterKitBusyProjectId,
        getProjectKitState,
        getProjectKitIconVisualState,
        getStarterKitIconTooltip,
      })}
      dataSource={projects}
      rowKey={getProjectRowKey}
      pagination={pagination}
      onRow={(record) => ({
        onClick: () => onOpenProject(record),
        style: { cursor: 'pointer' },
      })}
      tableLayout="fixed"
      scroll={{ x: 'max-content' }}
    />
  );
}

export function ProjectCardPagination({ current, total, pageSize, onChange }) {
  if (total <= pageSize) return null;
  return (
    <div style={{ marginTop: '16px', marginBottom: '16px', display: 'flex', justifyContent: 'flex-start', paddingLeft: 16 }}>
      <Pagination
        current={current}
        total={total}
        pageSize={pageSize}
        onChange={onChange}
        showSizeChanger={false}
        size="default"
        style={{ fontSize: '16px' }}
      />
    </div>
  );
}
